import pull from 'lodash/pull'
import includes from 'lodash/includes'
import find from 'lodash/find'
import filter from 'lodash/filter'
import compact from 'lodash/compact'
import React from 'react'
import { appSettingQuery, appSettingsTemplatesFavoriteMutationQuery } from './graphql'
import { useDocument, useMutation, useUser } from '@ztechapps/common.hook'
import type { AppSetting, UpdateTemplatesFavoriteServiceArgs } from '@ztechapps/common.contract'

export type UseFavoriteReportsResult = {
  favoriteList: any[]
  removeFavorite: any
  addFavorite: any
  isInFavorites: any
}
/**
 * This manages the favorites reports (adding, removing), showing the favorite list
 * This requires that the list of templates are already return, and so it will just exclude from the list the item that should not be added
 */
export const useFavoriteReports = ({ templateList }): UseFavoriteReportsResult => {
  const { user } = useUser()
  const userId = user?._id

  const appSettingFavorites = useDocument<AppSetting>({
    ...appSettingQuery.singleConfig,
    filter: {
      key: 'REPORT_TEMPLATES_FAVORITE',
      'scope.key': 'user',
      'scope.name': userId,
    },
    options: {
      queryOptions: {
        fetchPolicy: 'cache-and-network',
      },
    },
  })

  const faveTemplatesString = appSettingFavorites?.document?.value
  const favoriteTemplateIds = React.useMemo(
    () => (faveTemplatesString ? faveTemplatesString?.split(',') : []),
    [faveTemplatesString]
  )
  const refreshFavoriteList = appSettingFavorites?.refetch

  const favoriteList = React.useMemo(
    () =>
      filter(templateList, (item) => {
        return includes(favoriteTemplateIds, item?._id)
      }),
    [templateList, favoriteTemplateIds]
  )

  const [updateFavorites] = useMutation<
    { appSettings_UpdateTemplatesFavorite: AppSetting },
    UpdateTemplatesFavoriteServiceArgs
  >({
    query: appSettingsTemplatesFavoriteMutationQuery,
  })

  const isInFavorites = React.useCallback(
    ({ templateId }) => {
      return find(favoriteList, (item) => {
        return item?._id === templateId
      })
    },
    [favoriteList]
  )

  const removeFavorite = React.useCallback(
    async (templateIdToRemove) => {
      let faveTemplatesIds = pull(favoriteTemplateIds, templateIdToRemove)

      faveTemplatesIds = compact(faveTemplatesIds)
      const newFaves = faveTemplatesIds?.toString()
      await updateFavorites({ input: { value: newFaves } })

      refreshFavoriteList?.()
    },
    [favoriteTemplateIds, updateFavorites, refreshFavoriteList]
  )

  const addFavorite = React.useCallback(
    async ({ templateId }) => {
      let faveTemplatesIds = [...favoriteTemplateIds]
      if (isInFavorites({ templateId })) {
        faveTemplatesIds = pull(faveTemplatesIds, templateId)
      } else {
        faveTemplatesIds.push?.(templateId)
      }

      faveTemplatesIds = compact(faveTemplatesIds)
      const newFaves = faveTemplatesIds?.toString()
      await updateFavorites({ input: { value: newFaves } })

      refreshFavoriteList?.()
    },
    [favoriteTemplateIds, isInFavorites, updateFavorites, refreshFavoriteList]
  )

  return { favoriteList, removeFavorite, addFavorite, isInFavorites }
}
